import React from "react";
import { useFormikContext } from "formik";
import _ from "lodash";
import ValidationEvaluator from "../ValidationEvaluator";
import FieldRenderType from "../enums/FieldRenderType";
import SingleLineTextFieldWrapper from "./SingleLineTextFieldWrapper";
import FormTextAreaFieldWrapper from "./TextAreaFieldWrapper";
import RadioFieldGroupWrapper from "./RadioFieldGroupWrapper";
import FormMoneyFieldWrapper from "./FormMoneyFieldWrapper";
import UrlFieldWrapper from "./UrlFieldWrapper";
import CheckboxFieldWrapper from "./CheckboxFieldWrapper";
import DateTimeFieldWrapper from "./DateTimeFieldWrapper";
import SingleSelectFieldWrapper from "./SingleSelectFieldWrapper";
import CheckboxGroupFieldWrapper from "./CheckboxGroupFieldWrapper";
import PhoneNumberFieldWrapper from "./PhoneNumberFieldWrapper";
import { RadioOptionsDisplayStyle } from "shared/form/enum";
import { RenderFieldConfigType, RenderFieldFromSchemaWrapperType } from "../types/FieldRenderFromSchemaType";
import OrganizationName from "shared/form/renderer/components/organizationName";
import { useCopies } from "shared/hooks/useCopies";
import FormImageDragFieldWrapper from "./FormImageDragFieldWrapper";
import BulkTicketQuantityFieldWrapper from "./BulkTicketQuantityFieldWrapper";
import FormLabel from "shared/form/fields/formLabel";
import DynamicFieldLabel from "./DynamicFieldLabel";
import FormGroup from "shared/form/fields/formGroup";
import FormCommunitySponsorField from "./communitySponsor/FormCommunitySponsorField";
import DivisionField from "./division";
import FormIntegerFieldWrapper from "./FormIntegerFieldWrapper";
import SectionSeparator from "./sectionSeparator";
import HtmlEditorFieldWrapper from "./HtmlEditorFieldWrapper";
import FieldRenderSubType from "shared/enums/FieldRenderSubType";
import CompetitorField from "./competitor";
import NestedCheckboxGroupFieldWrapper from "./NestedCheckboxGroupFieldWrapper";
import UrlWithIconFieldWrapper from "./UrlWithIconFieldWrapper";
import SingleImageFieldWrapper from "./SingleImageFieldWrapper";
import MultiImageFieldWrapper from "./MultiImageFieldWrapper";
import TagFieldWrapper from "./TagFieldWrapper";
import MultiFileFieldWrapper from "./MultiFileFieldWrapper";
import EmailListFieldWrapper from "./EmailListFieldWrapper";
import EmailFieldWrapper from "./EmailFieldWrapper";
import PrivateMultiFileFieldWrapper from "./PrivateMultiFileFieldWrapper";
import OrganizationLevelField from "./organizationLevel";
import SingleImageFieldWithAspectRatioWrapper from "./SingleImageFieldWithAspectRatioWrapper";
import FormLabelWithActionWrapper from "./FormLabelWithActionWrapper";

/**
 * @name RenderFieldFromSchemaWrapper
 * @param props
 * @constructor
 */
export default function RenderFieldFromSchemaWrapper(props: RenderFieldFromSchemaWrapperType) {
  const copies = useCopies();
  const validationFn = React.useCallback(
    fieldValue => {
      return ValidationEvaluator.getValidationForSchema({
        schemaObj: props.schemaObj,
        schemaList: props.schemaList,
        fieldValue,
        formValues: props.formValues,
        copies
      });
    },
    [props.schemaObj, props.formValues, copies, props.schemaList]
  );
  return (
    <RenderFieldFromSchema
      validationFn={validationFn}
      schemaObj={props.schemaObj}
      skipResetToInitialValue={props.skipResetToInitialValue}
    />
  );
}

/**
 * @name RenderFieldFromSchema
 * @interface RenderFieldConfigType
 * @param props
 * @constructor
 */
function RenderFieldFromSchema(props: RenderFieldConfigType) {
  const { initialValues, setFieldValue } = useFormikContext();

  React.useEffect(() => {
    return () => {
      if (props.skipResetToInitialValue) {
        return;
      }
      const initialVal = _.get(initialValues, props.schemaObj.id);
      setFieldValue(props.schemaObj.id, initialVal);
    };
  }, [props.schemaObj.id, setFieldValue, initialValues, props.skipResetToInitialValue]);

  return React.useMemo(() => {
    return <RenderFieldsByRenderType validationFn={props.validationFn} schemaObj={props.schemaObj} />;
  }, [props.validationFn, props.schemaObj]);
}

function RenderFieldsByRenderType({ schemaObj, validationFn }: RenderFieldConfigType) {
  switch (schemaObj.renderType) {
    case FieldRenderType.SINGLE_LINE_TEXTFIELD:
      return <SingleLineTextFieldWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.EMAIL:
      return <EmailFieldWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.ORGANIZATION_NAME:
      return <OrganizationName schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.MULTI_LINE_TEXTFIELD:
      return <FormTextAreaFieldWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.PHONE:
      return <PhoneNumberFieldWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.SINGLE_SELECT:
      return <SingleSelectFieldWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.MULTI_SELECT:
      if (schemaObj.renderSubType === FieldRenderSubType.COMPETITOR) {
        return <CompetitorField schemaObj={schemaObj} validationFn={validationFn} />;
      }
      return <CheckboxGroupFieldWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.RADIO:
      return (
        <RadioFieldGroupWrapper
          displayStyle={RadioOptionsDisplayStyle.HORIZONTAL}
          schemaObj={schemaObj}
          validationFn={validationFn}
        />
      );
    case FieldRenderType.MONEY:
      return <FormMoneyFieldWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.INTEGER:
      return <FormIntegerFieldWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.URL:
      return <UrlFieldWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.URL_WITH_ICON:
      return <UrlWithIconFieldWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.CHECK_BOX:
      return <CheckboxFieldWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.DATE:
    case FieldRenderType.DATE_TIME:
      return (
        <DateTimeFieldWrapper
          schemaObj={schemaObj}
          validationFn={validationFn}
          formattingOptions={{ showTime: schemaObj.renderType === FieldRenderType.DATE_TIME }}
        />
      );
    case FieldRenderType.MM_DD:
      return (
        <DateTimeFieldWrapper
          schemaObj={schemaObj}
          validationFn={validationFn}
          formattingOptions={{ showYear: false }}
        />
      );
    case FieldRenderType.IMAGE:
      return <FormImageDragFieldWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.SINGLE_IMAGE:
      return <SingleImageFieldWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.SINGLE_IMAGE_WITH_ASPECT_RATIO:
      return <SingleImageFieldWithAspectRatioWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.MULTI_IMAGE:
      return <MultiImageFieldWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.MULTI_FILE:
      return <MultiFileFieldWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.PRIVATE_MULTI_FILE:
      return <PrivateMultiFileFieldWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.BULK_TICKET_QUANTITY:
      return <BulkTicketQuantityFieldWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.HTML:
      return (
        <FormGroup fullWidth={true} indented={schemaObj.indented}>
          <FormLabel label={<DynamicFieldLabel schemaObj={schemaObj} />} />
        </FormGroup>
      ); // todo:SK check for gaps and if any additional change is required to be made
    case FieldRenderType.COMMUNITY_SPONSOR:
      return <FormCommunitySponsorField schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.DIVISION:
      return <DivisionField schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.SECTION_SEPARATOR:
      return <SectionSeparator schemaObj={schemaObj} />;
    case FieldRenderType.MULTI_LINE_TEXTFIELD_HTML_INPUT:
      return <HtmlEditorFieldWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.NESTED_MULTI_SELECT:
      return <NestedCheckboxGroupFieldWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.TAGS:
      return <TagFieldWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.EMAIL_LIST:
      return <EmailListFieldWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.ORGANIZATION_LEVEL:
      return <OrganizationLevelField schemaObj={schemaObj} validationFn={validationFn} />;
    case FieldRenderType.LABEL_WITH_ACTION:
      return <FormLabelWithActionWrapper schemaObj={schemaObj} validationFn={validationFn} />;
    default:
      return null;
  }
}
